import { useTranslation } from "react-i18next";
import { Alert, AlertTitle, Box, Button, Grid, Stack } from "@mui/material";
import { InfoOutlined as InfoIcon, DescriptionOutlined as DescriptionIcon, LocationOnOutlined as LocationIcon } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { Location } from "@emberly/zenith-client";
import { useStation } from "../../../../providers/StationProvider";
import SectionCard, { SectionDivider, SectionRow, SectionSubHeader } from "../common/SectionCard";
import { MissionEnumsLists, MissionRequestEnumLists, MissionRequestEnums } from "../../../../common/constants";
import { TimeView } from "../../../common/core/Time";
import { FormatLocation } from "../../../../common/maphelpers";
import { useMemo } from "react";
import Route from "../../../../common/Route";
import TaskMapSection from "../../../common/map/TaskMapSection"
import { useNavigate } from "react-router";
import { useDevice } from "../../../../providers/DeviceProvider";

const useStyles = makeStyles()(
  (theme) => ({
    minHeightItem: {
      minHeight: "100%"
    },
    map: {
      marginTop: theme.spacing(2)
    }
  })
);

export default function MissionRequestSection(props) {
  const { entity, onAcceptRequest, onRejectRequest, openButton, station: oStation, isPreview, sent } = props;

  const { t } = useTranslation();
  const { classes } = useStyles();
  const { station: myStation } = useStation();
  const { slim } = useDevice();
  const station = oStation || myStation;

  const navigate = useNavigate();

  let task = entity?.salvageTasks?.[0];

  let { route, missionLocation, destination } = useMemo(() => {

    let waypoints = task?.route?.waypoints || [];

    let missionLocation = waypoints[0];
    let destination = waypoints.length > 1 ? waypoints[waypoints.length - 1] : null;

    let stationLocation = !!station.location ? new Location(station.location) : null;
    let r = new Route(waypoints.map(t => new Location(t)), stationLocation);

    return {
      route: r,
      missionLocation,
      destination
    }
  }, [task, station]);


  let actors = entity.actors;
  let owner = actors.owner;
  let requester = actors.requester;

  return (
    <Box>
      <Grid container spacing={2}>

        {
          entity.response === MissionRequestEnums.ResponseState.Accepted ? (
            <Grid item xs={12}>
              <Alert
                severity="success"
                action={
                  openButton ? (
                    <Button color="inherit" onClick={() => navigate(`/network/requests/${sent ? "sent" : "received"}/accepted/${sent ? entity.senderMissionId : entity.missionId}`, { relative: "path" })}>
                      {slim ? t("missionRequests:dialog:openShort") : t("missionRequests:dialog:openMission")}
                    </Button>
                  ) : null
                }
              >
                <AlertTitle>
                  {t("missionRequests:dialog:accepted")} <TimeView time={entity.responded} /> {t("missionRequests:dialog:by")} {entity?.responder?.name || "-"}
                </AlertTitle>
                {t("missionRequests:dialog:response")} {(entity.sourceType === MissionRequestEnums.SourceType.Form ? entity.email : entity.source?.name) || "-"}{!!entity.responseMessage ? `: "${entity.responseMessage}"` : ""}
              </Alert>
            </Grid>
          ) : entity.response === MissionRequestEnums.ResponseState.Rejected ? (
            <Grid item xs={12}>
              <Alert
                severity="error"
                action={
                  openButton && !!sent ? (
                    <Button color="inherit" onClick={() => navigate(`/network/requests/${sent ? "sent" : "received"}/accepted/${sent ? entity.senderMissionId : entity.missionId}`, { relative: "path" })}>
                      {slim ? t("missionRequests:dialog:openShort") : t("missionRequests:dialog:openMission")}
                    </Button>
                  ) : null
                }
              >
                <AlertTitle>
                  {t("missionRequests:dialog:rejected")} <TimeView time={entity.responded} /> {t("missionRequests:dialog:by")} {entity?.responder?.name || "-"}
                </AlertTitle>
                {t("missionRequests:dialog:response")} {(entity.sourceType === MissionRequestEnums.SourceType.Form ? entity.email : entity.source?.name) || "-"}{!!entity.responseMessage ? `: "${entity.responseMessage}"` : ""}
              </Alert>
            </Grid>
          ) : sent ? (
            <Grid item xs={12}>
              <Alert
                severity="info"
                variant="standard"
                action={
                  openButton ? (
                    <Button sx={{ marginTop: -0.5 }} color="inherit" onClick={() => navigate(`/network/requests/${sent ? "sent" : "received"}/new/${sent ? entity.senderMissionId : entity.missionId}`, { relative: "path" })}>
                      {slim ? t("missionRequests:dialog:openShort") : t("missionRequests:dialog:openMission")}
                    </Button>
                  ) : null
                }
              >
                {t("missionRequests:dialog:requested")} <TimeView time={entity.created} /> {t("missionRequests:dialog:by")} {entity?.sender || "-"}
              </Alert>
            </Grid>
          ) : null
        }

        <Grid item lg={6} xs={12}>
          <Stack spacing={2}>

            <SectionCard
              title={t("missionRequests:dialog:orderSection:title")}
              icon={<InfoIcon />}
            >
              {
                sent ? (
                  <SectionRow title={t("missionRequests:dialog:orderSection:receiver")}>
                    {t(`missionRequests:enums:sourceType:${MissionRequestEnumLists.SourceType[entity.sourceType]}`)} - {station?.displayName || "-"}
                  </SectionRow>
                ) : (
                  <SectionRow title={t("missionRequests:dialog:orderSection:source")}>
                    {t(`missionRequests:enums:sourceType:${MissionRequestEnumLists.SourceType[entity.sourceType]}`)} - {entity.source?.name}
                  </SectionRow>
                )
              }

              {
                !isPreview || sent ? (
                  <SectionRow title={t("missionRequests:dialog:orderSection:created")}>
                    <TimeView time={entity.created} />
                  </SectionRow>
                ) : null
              }


              <SectionRow title={t("missionRequests:dialog:orderSection:customerName")}>
                {entity.sender || "-"}
              </SectionRow>

              {
                sent ? (
                  <>
                    <SectionRow title={t("missionRequests:dialog:orderSection:receiverPhone")}>
                      {station?.supportPhone || "-"}
                    </SectionRow>

                    <SectionRow title={t("missionRequests:dialog:orderSection:receiverEmail")}>
                      {station?.supportEmail || "-"}
                    </SectionRow>
                  </>
                ) : (
                  <>
                    <SectionRow title={t("missionRequests:dialog:orderSection:customerPhone")}>
                      {entity.phoneNumber || "-"}
                    </SectionRow>

                    <SectionRow title={t("missionRequests:dialog:orderSection:customerEmail")}>
                      {entity.email || "-"}
                    </SectionRow>
                  </>
                )
              }


              <SectionRow title={t("missionRequests:dialog:orderSection:message")}>
                {entity.message || "-"}
              </SectionRow>

            </SectionCard>

            <SectionCard
              title={t("missionRequests:dialog:detailsSection:title")}
              icon={<DescriptionIcon />}
            >

              <SectionSubHeader title={t("missionRequests:dialog:detailsSection:details")} />


              <SectionRow title={t(t("missionRequests:dialog:detailsSection:missionDetails"))}>
                {entity?.details?.description || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:orderSection:missionType")}>
                {t(`mission:enums:type:${MissionEnumsLists.Type[entity.details?.type || 0]}`)}
              </SectionRow>

              <SectionDivider />

              <SectionSubHeader title={t("missionRequests:dialog:detailsSection:target")} />

              <SectionRow title={t("missionRequests:dialog:detailsSection:targetType")}>
                {t(`mission:enums:targetType:${MissionEnumsLists.TargetType[entity.target?.type || 0]}`)}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:registration")}>
                {entity.target?.carDetails?.registration || entity.target?.boatDetails?.registration || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:make")}>
                {entity.target?.carDetails?.make || entity.target?.boatDetails?.make || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:model")}>
                {entity.target?.carDetails?.model || entity.target?.boatDetails?.model || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:details")}>
                {entity.target?.description || "-"}
              </SectionRow>

              <SectionDivider />

              <SectionSubHeader title={t("missionRequests:dialog:detailsSection:requester")} />

              <SectionRow title={t("missionRequests:dialog:detailsSection:name")}>
                {requester?.name || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:address")}>
                {FormatLocation(requester?.location) || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:email")}>
                {requester?.email || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:phone")}>
                {requester?.phoneNumber || "-"}
              </SectionRow>

              <SectionDivider />

              <SectionSubHeader title={t("missionRequests:dialog:detailsSection:owner")} />

              <SectionRow title={t("missionRequests:dialog:detailsSection:name")}>
                {owner?.name || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:address")}>
                {FormatLocation(owner?.location) || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:email")}>
                {owner?.email || "-"}
              </SectionRow>

              <SectionRow title={t("missionRequests:dialog:detailsSection:phone")}>
                {owner?.phoneNumber || "-"}
              </SectionRow>


              {
                !!entity?.actors?.contactPerson?.name ? (
                  <>

                    <SectionDivider />

                    <SectionSubHeader title={t("missionRequests:dialog:detailsSection:contact")} />

                    <SectionRow title={t("missionRequests:dialog:detailsSection:name")}>
                      {entity?.actors?.contactPerson?.name || "-"}
                    </SectionRow>

                    <SectionRow title={t("missionRequests:dialog:detailsSection:email")}>
                      {entity?.actors?.contactPerson?.email || "-"}
                    </SectionRow>

                    <SectionRow title={t("missionRequests:dialog:detailsSection:phone")}>
                      {entity?.actors?.contactPerson?.phoneNumber || "-"}
                    </SectionRow>




                  </>
                ) : null
              }



            </SectionCard>
          </Stack>
        </Grid>

        {
          !!task ? (
            <Grid item lg={6} xs={12} className={classes.minHeightItem}>
              <SectionCard
                className={classes.minHeightItem}
                title={t("missionRequests:dialog:taskSection:title")}
                icon={<LocationIcon />}
                footer={
                  <TaskMapSection
                    className={classes.map}
                    route={route}
                    routeRevision={1}
                    station={station}
                  />
                }
              >

                <SectionRow title={t("missionRequests:dialog:taskSection:location")}>
                  {!!missionLocation ? FormatLocation(missionLocation) : "-"}
                </SectionRow>

                {
                  !!destination ? (
                    <SectionRow title={t("missionRequests:dialog:taskSection:destination")}>
                      {FormatLocation(destination) || "-"}
                    </SectionRow>
                  ) : null
                }

                <SectionRow title={t("missionRequests:dialog:taskSection:customerWaiting")}>
                  {t(`mission:enums:locationState:${MissionEnumsLists.LocationState[task.locationState]}`)}
                </SectionRow>

                <SectionRow title={t("missionRequests:dialog:taskSection:description")}>
                  {task.description}
                </SectionRow>

                <SectionRow title={t("missionRequests:dialog:taskSection:expectedDone")}>
                  {entity.expectedDone || "-"}
                </SectionRow>

              </SectionCard>
            </Grid>
          ) : null
        }

        {
          entity.response === MissionRequestEnums.ResponseState.New && !sent && !isPreview ? (
            <Grid item xs={12}>
              <Grid container spacing={1} justifyContent="flex-end">

                <Grid item>
                  <Button color="error" onClick={onRejectRequest}>{t("missionRequests:dialog:reject")}</Button>
                </Grid>

                <Grid item>
                  <Button variant="contained" color="primary" onClick={onAcceptRequest}>{t("missionRequests:dialog:accept")}</Button>
                </Grid>

              </Grid>
            </Grid>
          ) : null
        }

      </Grid>
    </Box>
  )
}
